import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { map, Observable, of, take } from "rxjs";
import { ApplicationContext } from "../models/application-context.model";
import { ApplicationContextService } from "../services/application-context.service";
import { resolveClinicAccess } from "../utils/organization-services-license.util";
import { shouldPromptCompanySelectionOnDirectLogin } from "../utils/account-organization.util";
import {
  getLaunchRequestedCompanyId,
  isLoginHandoffPending,
  shouldBypassClinicLicenseForLaunch,
} from "../utils/clinic-launch-context.util";

@Injectable({ providedIn: "root" })
export class ClinicLicenseGuard implements CanActivate {
  constructor(
    private readonly _applicationContextService: ApplicationContextService,
    private readonly _router: Router
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const snap = this._applicationContextService.contextSnapshot;
    if (snap?.currentlyLoggedInUser?.id) {
      return of(this.allowOrRedirect(snap, state.url));
    }
    return this._applicationContextService.context.pipe(
      take(1),
      map((ctx) => this.allowOrRedirect(ctx, state.url))
    );
  }

  private allowOrRedirect(
    context: ApplicationContext,
    url: string
  ): boolean | UrlTree {
    const path = (url || "").split("?")[0];
    if (path.startsWith("/select-company") || path.startsWith("/app-redirect")) {
      return true;
    }

    if (
      isLoginHandoffPending() &&
      shouldPromptCompanySelectionOnDirectLogin({
        userCompanies: context.userCompanies,
        isFromAyaLaunch: false,
      })
    ) {
      return this._router.createUrlTree(["/select-company"], {
        queryParams: { reason: "multi-org" },
      });
    }

    if (
      shouldBypassClinicLicenseForLaunch(
        context.currentlyLoggedInUser?.companyId
      )
    ) {
      return true;
    }

    const decision = resolveClinicAccess(context);
    if (decision === "allowed") {
      return true;
    }
    if (decision === "select-company") {
      const requestedCompanyId = getLaunchRequestedCompanyId();
      return this._router.createUrlTree(["/select-company"], {
        queryParams: {
          reason: "no-clinic-license",
          ...(requestedCompanyId
            ? { requestedCompanyId }
            : {}),
        },
      });
    }
    return this._router.createUrlTree(["/no-clinic-access"]);
  }
}
